import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { PageFailed } from "@/client/components/route-states";
import { TableShapeSkeleton } from "@/client/components/table-skeleton";
import { pageTitle } from "@/client/lib/page-title";
import { PENDING_MS } from "@/client/lib/pending";
import { holidaysQuery, scheduleQuery } from "@/client/lib/schedule-query";
import { ensureTenantData, requireModule } from "@/client/lib/session";
import { useNow } from "@/client/lib/use-now";
import {
  monthQuery,
  myTodayQuery,
} from "@/features/attendance/attendance-query";
import { MonthForPerson } from "@/features/attendance/components/month-for-person";
import { attendanceCopy } from "@/features/attendance/copy";
import { dayIn, monthIn } from "@/features/attendance/rules";
import { myMonthSearch } from "@/features/attendance/schemas";
import { ensureMonthLeave } from "@/features/leave/leave-query";
import { useMonthLeave } from "@/features/leave/use-month-leave";
import { peopleQuery } from "@/features/people/people-query";
import { profileQuery } from "@/features/settings/settings-query";

/** An open day's hours run to now, so the month re-reads the clock twice a minute. */
const TICK = 30_000;

export const Route = createFileRoute(
  "/_authenticated/$slug/attendance/my-month",
)({
  pendingMs: PENDING_MS,
  // A missing month is the current one in the company's timezone, never the browser's.
  validateSearch: myMonthSearch,
  loaderDeps: ({ search }) => ({ month: search.month }),
  beforeLoad: ({ context }) => requireModule(context, "attendance"),
  loader: async ({ context, deps }) => {
    const now = new Date();
    const month = deps.month ?? monthIn(context.companyTimezone, now);
    const year = Number(month.slice(0, 4));
    const leaveOn = context.companyModules.includes("leave");
    const [{ personId }] = await Promise.all([
      ensureTenantData(context.queryClient, profileQuery),
      ensureTenantData(context.queryClient, peopleQuery),
      ensureTenantData(context.queryClient, monthQuery(month)),
      ensureTenantData(context.queryClient, scheduleQuery),
      ensureTenantData(context.queryClient, holidaysQuery(year)),
      ensureTenantData(
        context.queryClient,
        myTodayQuery(dayIn(context.companyTimezone, now)),
      ),
    ]);
    // A module that is off makes no request.
    if (leaveOn) await ensureMonthLeave(context.queryClient, personId, month);
  },
  head: ({ match }) => ({
    meta: [
      { title: pageTitle(attendanceCopy.title, match.context.companyName) },
    ],
  }),
  pendingComponent: () => <TableShapeSkeleton />,
  errorComponent: PageFailed,
  component: MyMonthPage,
});

function MyMonthPage() {
  const { slug } = Route.useParams();
  const search = Route.useSearch();
  const { companyTimezone, companyModules } = Route.useRouteContext();
  const now = useNow(TICK);
  const month = search.month ?? monthIn(companyTimezone, now);
  const { data: people } = useSuspenseQuery(peopleQuery);
  const {
    data: { personId },
  } = useSuspenseQuery(profileQuery);
  const { leave, leaveTypes } = useMonthLeave(
    personId,
    month,
    companyModules.includes("leave"),
  );

  return (
    <MonthForPerson
      personId={personId}
      month={month}
      own
      timeZone={companyTimezone}
      now={now}
      slug={slug}
      people={people}
      leave={leave}
      leaveTypes={leaveTypes}
    />
  );
}
